/**
 * NotificationCenter — bell icon with unread badge + popover panel.
 * Polls /api/v1/notifications/alerts/ every 30 s.
 */
import { useEffect, useRef, useState, } from "react";
import { useNavigate } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  Bell,
  CheckCheck,
  Dumbbell,
  Calendar,
  CreditCard,
  ClipboardCheck,
  Info,
  Receipt,
  ThumbsUp,
  ThumbsDown,
  Snowflake,
  XCircle,
} from "lucide-react";
import { formatDistanceToNow } from "date-fns";
import { listStaffAlerts, markAlertsRead, approvePtBooking, rejectPtBooking, approveClassBooking, rejectClassBooking, type AlertCategory, type StaffAlert } from "@/lib/api/alerts";
import { getSelfServiceRequests, type SelfServiceRequest } from "@/lib/api/subscriptions";
import { cn } from "@/lib/utils/cn";
import { useHasPermission } from "@/lib/auth/permissions";

const CATEGORY_ICON: Record<AlertCategory, React.FC<{ className?: string }>> = {
  pt:          ({ className }) => <Dumbbell className={className} />,
  class:       ({ className }) => <Calendar className={className} />,
  membership:  ({ className }) => <CreditCard className={className} />,
  approval:    ({ className }) => <ClipboardCheck className={className} />,
  credit_sale: ({ className }) => <Receipt className={className} />,
  general:     ({ className }) => <Info className={className} />,
};

const CATEGORY_COLOR: Record<AlertCategory, string> = {
  pt:          "bg-violet-100 text-violet-600 dark:bg-violet-900/40 dark:text-violet-300",
  class:       "bg-blue-100 text-blue-600 dark:bg-blue-900/40 dark:text-blue-300",
  membership:  "bg-emerald-100 text-emerald-600 dark:bg-emerald-900/40 dark:text-emerald-300",
  approval:    "bg-amber-100 text-amber-600 dark:bg-amber-900/40 dark:text-amber-300",
  credit_sale: "bg-orange-100 text-orange-600 dark:bg-orange-900/40 dark:text-orange-300",
  general:     "bg-ink-100 text-ink-500 dark:bg-ink-800 dark:text-ink-400",
};

const PT_RESOLVED = new Set(["pt.booking.approved", "pt.booking.rejected"]);
const CLASS_RESOLVED = new Set(["classes.booking.approved", "classes.booking.rejected"]);

function BellPtQuickActions({ alert, allAlerts, onDone }: { alert: StaffAlert; allAlerts: StaffAlert[]; onDone: () => void }) {
  const canManage = useHasPermission("pt.booking.write");
  const qc = useQueryClient();
  const [done, setDone] = useState(false);

  const approveMut = useMutation({
    mutationFn: () => approvePtBooking(alert.action_id),
    onSuccess: () => { setDone(true); qc.invalidateQueries({ queryKey: ["staff-alerts"] }); onDone(); },
  });
  const rejectMut = useMutation({
    mutationFn: () => rejectPtBooking(alert.action_id),
    onSuccess: () => { setDone(true); qc.invalidateQueries({ queryKey: ["staff-alerts"] }); onDone(); },
  });

  const alreadyResolved = allAlerts.some(
    (a) => a.action_id === alert.action_id && PT_RESOLVED.has(a.event_type)
  );

  if (done || alreadyResolved || !canManage || !alert.action_id || alert.event_type !== "pt.booking.created") return null;
  const busy = approveMut.isPending || rejectMut.isPending;

  return (
    <div className="flex gap-1.5 mt-2" onClick={(e) => e.stopPropagation()}>
      <button type="button" disabled={busy} onClick={() => approveMut.mutate()}
        className="flex items-center gap-1 rounded-md bg-emerald-50 dark:bg-emerald-900/20 text-emerald-700 dark:text-emerald-300 border border-emerald-200 dark:border-emerald-700 px-2 py-1 text-[11px] font-semibold hover:bg-emerald-100 disabled:opacity-50 transition-colors">
        <ThumbsUp className="h-3 w-3" />
        {approveMut.isPending ? "…" : "Approve"}
      </button>
      <button type="button" disabled={busy} onClick={() => rejectMut.mutate()}
        className="flex items-center gap-1 rounded-md bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-300 border border-red-200 dark:border-red-700 px-2 py-1 text-[11px] font-semibold hover:bg-red-100 disabled:opacity-50 transition-colors">
        <ThumbsDown className="h-3 w-3" />
        {rejectMut.isPending ? "…" : "Reject"}
      </button>
    </div>
  );
}

function BellClassBookingQuickActions({ alert, allAlerts, onDone }: { alert: StaffAlert; allAlerts: StaffAlert[]; onDone: () => void }) {
  const canManage = useHasPermission("classes.booking.write");
  const qc = useQueryClient();
  const [done, setDone] = useState(false);

  const approveMut = useMutation({
    mutationFn: () => approveClassBooking(alert.action_id),
    onSuccess: () => { setDone(true); qc.invalidateQueries({ queryKey: ["staff-alerts"] }); onDone(); },
  });
  const rejectMut = useMutation({
    mutationFn: () => rejectClassBooking(alert.action_id),
    onSuccess: () => { setDone(true); qc.invalidateQueries({ queryKey: ["staff-alerts"] }); onDone(); },
  });

  const alreadyResolved = allAlerts.some(
    (a) => a.action_id === alert.action_id && CLASS_RESOLVED.has(a.event_type)
  );

  if (done || alreadyResolved || !canManage || !alert.action_id || alert.event_type !== "classes.booking.requested") return null;
  const busy = approveMut.isPending || rejectMut.isPending;

  return (
    <div className="flex gap-1.5 mt-2" onClick={(e) => e.stopPropagation()}>
      <button type="button" disabled={busy} onClick={() => approveMut.mutate()}
        className="flex items-center gap-1 rounded-md bg-emerald-50 dark:bg-emerald-900/20 text-emerald-700 dark:text-emerald-300 border border-emerald-200 dark:border-emerald-700 px-2 py-1 text-[11px] font-semibold hover:bg-emerald-100 disabled:opacity-50 transition-colors">
        <ThumbsUp className="h-3 w-3" />
        {approveMut.isPending ? "…" : "Approve"}
      </button>
      <button type="button" disabled={busy} onClick={() => rejectMut.mutate()}
        className="flex items-center gap-1 rounded-md bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-300 border border-red-200 dark:border-red-700 px-2 py-1 text-[11px] font-semibold hover:bg-red-100 disabled:opacity-50 transition-colors">
        <ThumbsDown className="h-3 w-3" />
        {rejectMut.isPending ? "…" : "Reject"}
      </button>
    </div>
  );
}

function CreditSaleBalancePills({ body }: { body: string }) {
  const paidMatch = body.match(/AED ([\d.]+) received/);
  const remainMatch = body.match(/AED ([\d.]+) remaining/);
  const settled = body.includes("fully settled");

  if (!paidMatch && !settled) return null;

  return (
    <div className="flex gap-1.5 mt-1.5 flex-wrap" onClick={(e) => e.stopPropagation()}>
      {paidMatch && (
        <span className="inline-flex items-center gap-1 rounded-full bg-emerald-50 dark:bg-emerald-900/20 text-emerald-700 dark:text-emerald-300 border border-emerald-200 dark:border-emerald-700 px-2 py-0.5 text-[11px] font-semibold">
          Paid AED {paidMatch[1]}
        </span>
      )}
      {settled ? (
        <span className="inline-flex items-center gap-1 rounded-full bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-300 border border-blue-200 dark:border-blue-700 px-2 py-0.5 text-[11px] font-semibold">
          Fully settled
        </span>
      ) : remainMatch ? (
        <span className="inline-flex items-center gap-1 rounded-full bg-amber-50 dark:bg-amber-900/20 text-amber-700 dark:text-amber-300 border border-amber-200 dark:border-amber-700 px-2 py-0.5 text-[11px] font-semibold">
          AED {remainMatch[1]} pending
        </span>
      ) : null}
    </div>
  );
}

function AlertRow({ alert, allAlerts, onNavigate, onRead }: { alert: StaffAlert; allAlerts: StaffAlert[]; onNavigate: () => void; onRead: (id: string) => void }) {
  const navigate = useNavigate();
  const Icon = CATEGORY_ICON[alert.category] ?? CATEGORY_ICON.general;

  function handleClick() {
    if (!alert.is_read) onRead(alert.id);
    onNavigate();
    if (alert.link) navigate(alert.link);
  }

  return (
    <div className={cn(
      "flex gap-3 px-4 py-3",
      !alert.is_read && "bg-primary-50/50 dark:bg-primary-900/10",
    )}>
      <div className={cn("mt-0.5 h-8 w-8 rounded-lg flex items-center justify-center shrink-0", CATEGORY_COLOR[alert.category])}>
        <Icon className="h-4 w-4" />
      </div>
      <div className="min-w-0 flex-1">
        <button type="button" onClick={handleClick}
          className="w-full text-left hover:opacity-80 transition-opacity">
          <div className="flex items-start justify-between gap-2">
            <p className={cn("text-sm leading-snug", !alert.is_read && "font-semibold text-ink-900 dark:text-ink-100")}>
              {alert.title}
            </p>
            {!alert.is_read && (
              <span className="mt-1 h-2 w-2 rounded-full bg-primary-500 shrink-0" />
            )}
          </div>
          {alert.body && (
            <p className="text-xs text-ink-500 dark:text-ink-400 mt-0.5 truncate">{alert.body}</p>
          )}
          <p className="text-xs text-ink-400 dark:text-ink-500 mt-1">
            {formatDistanceToNow(new Date(alert.created_at), { addSuffix: true })}
          </p>
        </button>
        <BellPtQuickActions alert={alert} allAlerts={allAlerts} onDone={() => onRead(alert.id)} />
        <BellClassBookingQuickActions alert={alert} allAlerts={allAlerts} onDone={() => onRead(alert.id)} />
        {alert.category === "credit_sale" && alert.event_type === "credit_sale.payment_received" && (
          <CreditSaleBalancePills body={alert.body} />
        )}
      </div>
    </div>
  );
}

function PendingRequestRow({ req, onNavigate }: { req: SelfServiceRequest; onNavigate: () => void }) {
  const navigate = useNavigate();
  const isFreeze = req.kind === "freeze";
  const Icon = isFreeze ? Snowflake : XCircle;
  const color = isFreeze
    ? "bg-cyan-100 text-cyan-600 dark:bg-cyan-900/40 dark:text-cyan-300"
    : "bg-red-100 text-red-600 dark:bg-red-900/40 dark:text-red-300";

  const detail = isFreeze
    ? `${req.payload.freeze_start} → ${req.payload.freeze_end}`
    : (req.payload as { reason?: string }).reason ?? "";

  function handleClick() {
    onNavigate();
    navigate("/subscriptions/review-queue");
  }

  return (
    <button
      type="button"
      onClick={handleClick}
      className="flex gap-3 px-4 py-3 w-full text-left hover:bg-amber-50/60 dark:hover:bg-amber-900/10 transition-colors"
    >
      <div className={cn("mt-0.5 h-8 w-8 rounded-lg flex items-center justify-center shrink-0", color)}>
        <Icon className="h-4 w-4" />
      </div>
      <div className="min-w-0 flex-1">
        <div className="flex items-start justify-between gap-2">
          <p className="text-sm font-semibold text-ink-900 dark:text-ink-100 leading-snug">
            {isFreeze ? "Freeze request" : "Cancel request"} — {req.member_name}
          </p>
          <span className="mt-0.5 h-2 w-2 rounded-full bg-amber-500 shrink-0" />
        </div>
        {detail && <p className="text-xs text-ink-500 dark:text-ink-400 mt-0.5 truncate">{detail}</p>}
        {req.requested_at && (
          <p className="text-xs text-ink-400 dark:text-ink-500 mt-1">
            {formatDistanceToNow(new Date(req.requested_at), { addSuffix: true })}
          </p>
        )}
      </div>
    </button>
  );
}

export function NotificationCenter() {
  const [open, setOpen] = useState(false);
  const panelRef = useRef<HTMLDivElement>(null);
  const qc = useQueryClient();

  const { data } = useQuery({
    queryKey: ["staff-alerts"],
    queryFn: listStaffAlerts,
    refetchInterval: 30_000,
    staleTime: 15_000,
  });

  const canReview = useHasPermission("subscriptions.selfservice.review");
  const { data: mcrData } = useQuery({
    queryKey: ["self-service-requests", "pending-bell"],
    queryFn: () => getSelfServiceRequests({ status: "pending" }),
    enabled: canReview,
    refetchInterval: 60_000,
    staleTime: 30_000,
  });
  const pendingMcrs = mcrData?.results ?? [];

  const markRead = useMutation({
    mutationFn: () => markAlertsRead(),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["staff-alerts"] }),
  });

  function handleReadOne(id: string) {
    qc.setQueryData<import("@/lib/api/alerts").StaffAlertsResponse>(["staff-alerts"], (old) => {
      if (!old) return old;
      const results = old.results.map((a) => a.id === id ? { ...a, is_read: true } : a);
      return { ...old, results, unread_count: Math.max(0, old.unread_count - 1) };
    });
    markAlertsRead([id]).catch(() => qc.invalidateQueries({ queryKey: ["staff-alerts"] }));
  }

  // Close on outside click
  useEffect(() => {
    if (!open) return;
    function handleClick(e: MouseEvent) {
      if (panelRef.current && !panelRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, [open]);

  const unread = (data?.unread_count ?? 0) + pendingMcrs.length;
  const alerts = data?.results ?? [];

  function handleOpen() {
    setOpen((v) => !v);
  }

  function handleMarkAllRead() {
    markRead.mutate();
  }

  return (
    <div className="relative" ref={panelRef}>
      {/* Bell button */}
      <button
        type="button"
        onClick={handleOpen}
        aria-label="Notification center"
        className="relative flex h-9 w-9 items-center justify-center rounded-lg text-ink-500 hover:bg-ink-100 hover:text-ink-700 dark:hover:bg-ink-800 dark:hover:text-ink-200 transition-colors"
      >
        <Bell className="h-5 w-5" />
        {unread > 0 && (
          <span className="absolute -top-0.5 -right-0.5 h-4.5 min-w-[18px] rounded-full bg-primary-600 px-1 text-[10px] font-bold text-white flex items-center justify-center leading-none">
            {unread > 99 ? "99+" : unread}
          </span>
        )}
      </button>

      {/* Panel */}
      {open && (
        <div className="absolute right-0 top-full mt-2 z-50 w-[360px] max-w-[calc(100vw-1rem)] rounded-xl border border-border-100 bg-white dark:bg-ink-900 shadow-xl overflow-hidden flex flex-col">
          {/* Header */}
          <div className="flex items-center justify-between px-4 py-3 border-b border-border-100">
            <div className="flex items-center gap-2">
              <Bell className="h-4 w-4 text-ink-500" />
              <span className="text-sm font-semibold text-ink-800 dark:text-ink-100">Notifications</span>
              {unread > 0 && (
                <span className="rounded-full bg-primary-100 dark:bg-primary-900/40 px-2 py-0.5 text-xs font-semibold text-primary-700 dark:text-primary-300">
                  {unread} new
                </span>
              )}
            </div>
            {unread > 0 && (
              <button
                type="button"
                onClick={handleMarkAllRead}
                disabled={markRead.isPending}
                className="flex items-center gap-1 text-xs text-primary-600 hover:text-primary-800 font-medium transition-colors"
              >
                <CheckCheck className="h-3.5 w-3.5" />
                Mark all read
              </button>
            )}
          </div>

          {/* Pending self-service requests section */}
          {pendingMcrs.length > 0 && (
            <div className="border-b border-border-100">
              <div className="px-4 pt-2 pb-1 flex items-center justify-between">
                <span className="text-[10px] font-bold uppercase tracking-widest text-amber-600 dark:text-amber-400">
                  Pending Approvals ({pendingMcrs.length})
                </span>
              </div>
              <div className="divide-y divide-border-100/60">
                {pendingMcrs.map((req) => (
                  <PendingRequestRow key={req.id} req={req} onNavigate={() => setOpen(false)} />
                ))}
              </div>
            </div>
          )}

          {/* Alert list */}
          <div className="overflow-y-auto max-h-[480px] divide-y divide-border-100/60">
            {alerts.length === 0 && pendingMcrs.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-12 text-center">
                <Bell className="h-8 w-8 text-ink-200 dark:text-ink-700 mb-3" />
                <p className="text-sm text-ink-400">All caught up!</p>
                <p className="text-xs text-ink-300 mt-1">No new notifications</p>
              </div>
            ) : (
              alerts.map((alert) => (
                <AlertRow
                  key={alert.id}
                  alert={alert}
                  allAlerts={alerts}
                  onNavigate={() => setOpen(false)}
                  onRead={handleReadOne}
                />
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
}
